import { useCallback, useLayoutEffect, useReducer } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { useEditorRemixLifecycle } from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';

import { setRemixThinkingLifecycle } from '../editor-commands/set-remix-thinking-lifecycle';
import type { MalleableUiPlugin } from '../malleableUiPluginType';

import { useEditorRemixLifecycleMirror } from './editorRemixLifecycleMirror';
import type { RemixThinkingLifecycleProjection } from './utils/editor-remix-lifecycle-projection';
import {
	getEditorRemixLifecyclePresentation,
	subscribeToEditorRemixLifecyclePresentation,
} from './utils/editor-remix-callback-registry';

/**
 * Sibling of `RemixContent`, not a descendant, so it survives the terminal cleanup that unmounts
 * that subtree. The mount in `malleableUiPlugin` already gates this on the state-machine cohort.
 */
export const EditorRemixEffectHost = ({
	api,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
}): null => {
	const { remixId } = useEditorRemixLifecycle();
	const [, refreshPresentation] = useReducer((count: number) => count + 1, 0);

	useLayoutEffect(
		() => subscribeToEditorRemixLifecyclePresentation(api, refreshPresentation),
		[api, remixId],
	);
	const presentation = getEditorRemixLifecyclePresentation(api);

	const dispatchProjection = useCallback(
		(projection: RemixThinkingLifecycleProjection) => {
			api?.core.actions.execute(setRemixThinkingLifecycle(projection));
		},
		[api],
	);

	useEditorRemixLifecycleMirror({
		animationFinished: presentation.animationFinished,
		presentationRemixId: presentation.remixId ?? null,
		dispatchProjection,
	});

	return null;
};
